import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCachedChain } from "../../../../chains/utils.js";
/**
 * @internal
 */
export function useSiweAuth(activeWallet, activeAccount, authOptions) {
    const requiresAuth = !!authOptions;
    const queryClient = useQueryClient();
    const isLoggedInQuery = useQuery({
        queryKey: ["siwe_auth", "isLoggedIn", activeAccount?.address],
        enabled: requiresAuth && !!activeAccount?.address,
        queryFn: () => {
            // these cases should never be hit but just in case...
            if (!authOptions || !activeAccount?.address) {
                return false;
            }
            return authOptions.isLoggedIn(activeAccount.address);
        },
        placeholderData: false,
        refetchOnWindowFocus: false,
    });
    const loginMutation = useMutation({
        mutationKey: ["siwe_auth", "login"],
        mutationFn: async () => {
            if (!authOptions) {
                throw new Error("No auth options provided");
            }
            if (!activeWallet) {
                throw new Error("No active wallet");
            }
            const chain = activeWallet.getChain();
            if (!chain) {
                throw new Error("No active chain");
            }
            if (!activeAccount) {
                throw new Error("No active account");
            }
            const [payload, { signLoginPayload }] = await Promise.all([
                authOptions.getLoginPayload({
                    address: activeAccount.address,
                    chainId: chain.id,
                }),
                // we lazy-load this because it's only needed when logging in
                import("../../../../auth/core/sign-login-payload.js"),
            ]);
            if (payload.chain_id) {
                await activeWallet.switchChain(getCachedChain(Number(payload.chain_id)));
            }
            const signedPayload = await signLoginPayload({
                payload,
                account: activeAccount,
            });
            return await authOptions.doLogin(signedPayload);
        },
        onSettled: () => {
            return queryClient.invalidateQueries({
                queryKey: ["siwe_auth", "isLoggedIn"],
            });
        },
    });
    const logoutMutation = useMutation({
        mutationKey: ["siwe_auth", "logout"],
        mutationFn: async () => {
            if (!authOptions) {
                throw new Error("No auth options provided");
            }
            return await authOptions.doLogout();
        },
        onSettled: () => {
            return queryClient.invalidateQueries({
                queryKey: ["siwe_auth", "isLoggedIn"],
            });
        },
    });
    return {
        // is auth even enabled
        requiresAuth,
        // login
        doLogin: loginMutation.mutateAsync,
        isLoggingIn: loginMutation.isPending,
        // logout
        doLogout: logoutMutation.mutateAsync,
        isLoggingOut: logoutMutation.isPending,
        // checking if logged in
        isLoggedIn: isLoggedInQuery.data,
        isLoading: isLoggedInQuery.isFetching,
    };
}
//# sourceMappingURL=useSiweAuth.js.map